class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function getSoliniaCartDrawer() {
  return document.querySelector('cart-drawer');
}

function renderSoliniaCartDrawer(parsedState) {
  const cartDrawer = getSoliniaCartDrawer();
  if (cartDrawer && parsedState && parsedState.sections && typeof cartDrawer.renderContents === 'function') {
    cartDrawer.renderContents(parsedState);
    return true;
  }
  return false;
}

function setSoliniaButtonLoading(button, loading) {
  if (!button) return;
  button.disabled = loading;
  button.classList.toggle('is-loading', loading);
}

function showSoliniaCartError(error) {
  const errors = document.getElementById('CartDrawer-CartErrors');
  if (errors) {
    errors.textContent = error && error.description ? error.description : window.cartStrings.error;
  }
}

window.SoliniaCartDrawer = {
  render: renderSoliniaCartDrawer,
  addJson(payload, button) {
    setSoliniaButtonLoading(button, true);
    const body = JSON.stringify({
      ...payload,
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });

    return fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body })
      .then((response) =>
        response.json().then((parsedState) => {
          if (!response.ok) throw parsedState;
          return parsedState;
        })
      )
      .then((parsedState) => {
        if (!renderSoliniaCartDrawer(parsedState)) window.location.href = routes.cart_url;
        return parsedState;
      })
      .catch((error) => {
        showSoliniaCartError(error);
        throw error;
      })
      .finally(() => {
        setSoliniaButtonLoading(button, false);
      });
  },
};

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.matches('form[action*="/cart/add"]')) return;
  if (form.hasAttribute('data-solinia-skip-drawer')) return;
  if (event.defaultPrevented) return;

  event.preventDefault();
  const button = event.submitter || form.querySelector('[type="submit"]');
  const formData = new FormData(form);
  formData.set('sections', 'cart-drawer,cart-icon-bubble');
  formData.set('sections_url', window.location.pathname);

  setSoliniaButtonLoading(button, true);

  fetch(form.getAttribute('action') || routes.cart_add_url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
    },
    body: formData,
  })
    .then((response) =>
      response.json().then((parsedState) => {
        if (!response.ok) throw parsedState;
        return parsedState;
      })
    )
    .then((parsedState) => {
      if (!renderSoliniaCartDrawer(parsedState)) window.location.href = routes.cart_url;
    })
    .catch((error) => {
      showSoliniaCartError(error);
      alert('Sorry, we could not add this item to your cart. Please try again.');
    })
    .finally(() => {
      setSoliniaButtonLoading(button, false);
    });
});

document.addEventListener('click', (event) => {
  const infoButton = event.target.closest('[data-solinia-upsell-info]');
  if (infoButton) {
    const modal = document.getElementById(infoButton.getAttribute('aria-controls') || 'SoliniaUpsellInfoModal');
    if (modal) {
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      modal.querySelector('.solinia-cart-modal__close')?.focus();
    }
    return;
  }

  if (event.target.closest('[data-solinia-upsell-modal-close]')) {
    const modal = event.target.closest('.solinia-cart-modal');
    if (modal) {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.toggle('overflow-hidden', Boolean(document.querySelector('cart-drawer.active')));
    }
    return;
  }

  const button = event.target.closest('[data-solinia-upsell-add]');
  if (!button || button.disabled) return;

  const variantId = button.getAttribute('data-variant-id');
  if (!variantId) return;

  window.SoliniaCartDrawer.addJson({ id: variantId, quantity: 1 }, button)
    .catch((error) => {
      console.error(error);
    });
});

document.addEventListener('keyup', (event) => {
  if (event.code !== 'Escape') return;
  const modal = document.querySelector('.solinia-cart-modal.is-open');
  if (!modal) return;
  modal.classList.remove('is-open');
  modal.setAttribute('aria-hidden', 'true');
  document.body.classList.toggle('overflow-hidden', Boolean(document.querySelector('cart-drawer.active')));
});

(() => {
  if (window.SoliniaRechargeCartOverridesLoaded || window.SoliniaRechargeCartOverridesBooted || document.querySelector('script[src*="solinia-recharge-cart-overrides-v15.js"]')) return;
  const currentScript = document.currentScript;
  const baseUrl = currentScript?.src || `${window.location.origin}/cdn/shop/t/7/assets/cart-drawer.js`;
  const script = document.createElement('script');
  script.src = new URL('solinia-recharge-cart-overrides-v15.js?v=20260517-speed-cart-gate-v16', baseUrl).href;
  script.defer = true;
  script.onload = () => {
    window.SoliniaRechargeCartOverridesLoaded = true;
  };
  document.head.appendChild(script);
})();
